import { fmtTraffic, labelFor, type DailyTraffic, type TrafficUnit } from './dailySeries';

/** What one reading of the chart is called in a sentence and in a column head.
 *  The two readings are the same days measured differently, so the alternative
 *  text says which one it is rather than leaving a table of numbers unlabelled. */
const WORDS: Record<TrafficUnit, { noun: string; head: string; sentence: (n: string) => string }> = {
  requests: {
    noun: 'Requests',
    head: 'Requests',
    sentence: (n) => `served ${n} customer requests`,
  },
  spend: {
    noun: 'Spend',
    head: 'Spend (USD)',
    sentence: (n) => `charged customers ${n} in total`,
  },
};

/**
 * The chart, said once in a form a machine can read.
 *
 * The plot itself is a run of stacked coloured boxes with no text in any of
 * them, so a crawler, an agent reading this page's Markdown, and a screen
 * reader all get nothing from it. The same numbers are stated here instead:
 * the window in a sentence, the platform totals, and one row per day.
 *
 * It is the same series the chart draws. There is no second source of truth.
 */
export const DailyTrafficData = ({ traffic }: { traffic: DailyTraffic }) => {
  const { unit, readings, totals } = traffic;
  const words = WORDS[unit];
  const first = readings[0]?.date ?? '';
  const last = readings[readings.length - 1]?.date ?? '';
  const total = readings.reduce((n, day) => n + day.total, 0);
  const busiest = readings.reduce(
    (best, day) => (day.total > best.total ? day : best),
    readings[0] ?? { date: '', total: 0, parts: [] },
  );

  return (
    <div className="sr-only">
      <p>
        {`AnyAPI ${words.sentence(fmtTraffic(unit, total))} over the ${readings.length} days from ${first} to ${last}. The biggest day was ${busiest.date}, at ${fmtTraffic(unit, busiest.total)}. Requests refused on the caller's own input are excluded.`}
      </p>
      <table>
        <caption>{`${words.noun} by platform over the window, biggest first`}</caption>
        <thead>
          <tr>
            <th scope="col">Platform</th>
            <th scope="col">{words.head}</th>
          </tr>
        </thead>
        <tbody>
          {totals.map((part) => (
            <tr key={part.id}>
              <th scope="row">{labelFor(part.id)}</th>
              <td>{fmtTraffic(unit, part.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table>
        <caption>{`${words.noun} per day, oldest day first`}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">{words.head}</th>
          </tr>
        </thead>
        <tbody>
          {readings.map((day) => (
            <tr key={day.date}>
              <th scope="row">{day.date}</th>
              <td>{fmtTraffic(unit, day.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
